import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// 3 minutes of inactivity → auto-logout. Tracks user input (mouse/keyboard/
// touch/scroll/visibilitychange) on the window so any meaningful interaction
// resets the countdown. Inactive logout only fires for logged-in sessions.
const IDLE_TIMEOUT_MS = 3 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("aam_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      localStorage.removeItem("aam_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("aam_token", data.token);
    setUser(data.user);
    return data.user;
  };

  /**
   * Register no longer auto-logs the user in. The backend now returns
   * `{ requires_verification: true, email, message, verification_link? }`
   * — the caller (Register page) shows a "Check your email" screen.
   */
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post("/auth/resend-verification", { email });
    return data;
  };

  /**
   * Replace the in-memory user (e.g. after editing profile via /api/users/me).
   * Server is the source of truth; pass it the freshly-returned UserPublic.
   */
  const updateUser = useCallback((freshUser) => {
    setUser(freshUser);
  }, []);

  const logout = useCallback((reason = "manual") => {
    localStorage.removeItem("aam_token");
    setUser(null);
    if (reason === "idle") {
      toast.info("Signed out after 3 minutes of inactivity", {
        description: "Sign in again to pick up where you left off.",
        duration: 6000,
      });
    }
  }, []);

  // ---- Idle auto-logout (v1.35) ------------------------------------------
  // Only arm the timer when the user is actually authenticated. Each input
  // event resets the deadline; when it elapses, logout(reason='idle') fires
  // and the toast tells the user why they got bounced.
  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (!user) return undefined;

    const scheduleLogout = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => logout("idle"), IDLE_TIMEOUT_MS);
    };
    const onActivity = () => scheduleLogout();
    scheduleLogout();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onActivity);
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener("visibilitychange", onActivity);
    };
  }, [user, logout]);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, resendVerification, updateUser }),
    [user, loading, refresh, updateUser, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
